<template>
  <b-row
    align-h="center"
    class="case-delayed-widget"
  >
    <b-col
      lg="12"
      xl="9"
      class="p-2"
    >
      <div class="item-text d-flex px-2 pb-2">
        <div class="d-flex align-items-center justify-content-center">
          <i
            :class="item.icon + ' mr-3 main'"
            aria-hidden="true"
          />
        </div>
        <div class="d-flex flex-column justify-content-start">
          <small>{{ $t('dashboard.widget.delays') }}</small>
          <span>{{ $t(item.label) }}</span>
        </div>
      </div>
      <div
        class="d-flex mt-1 row justify-content-around"
        :class="{'px-3': !switchUpdateMode}"
      >
        <b-row
          v-if="switchUpdateMode"
          class="px-4 mb-2"
        >
          <b-col>
            <p class="help-text dark">
              {{ $t('dashboard.widget.delaysText') }}
            </p>
          </b-col>
        </b-row>
        <b-row class="w-100">
          <b-col>
            <div class="mt-3">
              <case-delayed-chart
                v-if="caseTypes.length"
                ref="caseDelayedChart"
                :case-delay-summary="caseDelaySummary"
              />
            </div>
          </b-col>
        </b-row>
        <div
          class="d-flex mt-3"
          :class="{'col-md-12': switchUpdateMode}"
        >
          <div
            :id="`pop-delays-laboratories${item.label}`"
            class="form-icon d-flex mx-2 is-relative"
          >
            <i
              class="fal fa-hospital mt-2"
              :class="{'filter-disabled': noFilters}"
              aria-hidden="true"
            />
            <div
              v-if="!noFilters"
              class="bubble"
            >
              <vtBubble
                :number="laboratoriesSelected.length"
                :info="true"
              />
            </div>
          </div>
          <b-popover
            v-if="!noFilters"
            :target="`pop-delays-laboratories${item.label}`"
            placement="right"
            triggers="hover"
          >
            <div class="px-3 py-2">
              <div class="mb-1">
                <span><strong>{{ $t('dashboard.widget.selectedCenters') }}</strong></span>
              </div>
              <div
                v-for="(lab, index) in laboratoriesSelected"
                :key="index"
              >
                {{ lab.labName }}
              </div>
            </div>
          </b-popover>
        </div>
        <div
          class="d-flex mt-3"
          :class="{'col-md-12': switchUpdateMode}"
        >
          <div
            :id="`pop-delays-urgency${item.label}`"
            class="form-icon d-flex mx-2 is-relative"
          >
            <i
              class="fal fa-flag mt-2"
              :class="{'filter-disabled': noFilters}"
              aria-hidden="true"
            />
            <div
              v-if="!noFilters"
              class="bubble"
            >
              <vtBubble
                :number="prioritiesSelected.length"
                :info="true"
              />
            </div>
          </div>
          <b-popover
            v-if="!noFilters"
            :target="`pop-delays-urgency${item.label}`"
            placement="right"
            triggers="hover"
          >
            <div class="px-3 py-2">
              <div class="mb-1">
                <span><strong>{{ $t('dashboard.widget.selectedPriorities') }}</strong></span>
              </div>
              <div
                v-for="(priority, index) in prioritiesSelected"
                :key="index"
              >
                {{ priority.name }}
              </div>
            </div>
          </b-popover>
        </div>
        <div
          class="d-flex mt-3"
          :class="{'col-md-12': switchUpdateMode}"
        >
          <div
            :id="`pop-delays-case-type${item.label}`"
            class="form-icon d-flex mx-2 is-relative"
          >
            <i
              class="fal fa-chart-network mt-2"
              :class="{'filter-disabled': noFilters}"
              aria-hidden="true"
            />
            <div
              v-if="!noFilters"
              class="bubble"
            >
              <vtBubble
                :number="caseTypesSelected.length"
                :info="true"
              />
            </div>
          </div>
          <b-popover
            v-if="!noFilters"
            :target="`pop-delays-case-type${item.label}`"
            placement="right"
            triggers="hover"
          >
            <div class="px-3 py-2">
              <div class="mb-1">
                <span><strong>{{ $t('dashboard.widget.selectedCaseTypes') }}</strong></span>
              </div>
              <div
                v-for="(caseType, index) in caseTypesSelected"
                :key="index"
              >
                {{ caseType.description }}
              </div>
            </div>
          </b-popover>
        </div>
        <div
          class="d-flex mt-3"
          :class="{'col-md-12': switchUpdateMode}"
        >
          <div
            :id="`pop-delays-case-situation${item.label}`"
            class="form-icon d-flex mx-2 is-relative"
          >
            <i
              class="fal fa-tags mt-2"
              :class="{'filter-disabled': noFilters}"
              aria-hidden="true"
            />
          </div>
          <b-popover
            v-if="!noFilters"
            :target="`pop-delays-case-situation${item.label}`"
            placement="right"
            triggers="hover"
          >
            <div class="px-3 py-2">
              <div class="mb-1">
                <span><strong>{{ $t('dashboard.widget.selectedStatus') }}</strong></span>
              </div>
              <div>
                {{ caseSituationSelectedLabel }}
              </div>
            </div>
          </b-popover>
        </div>
        <b-button
          v-if="update"
          v-tooltip="$t('dashboard.widget.widgetSettings')"
          class="btn btn__icon btn__icon--primary mt-3 px-1 py-0 btn-secondary"
          @click="toggleWidgetConfigurationSidepanel"
        >
          <i
            class="far fa-sliders-h"
            aria-hidden="true"
          />
        </b-button>
      </div>
    </b-col>
  </b-row>
</template>

<script>
import VtBubble from '@/components/common/VtBubble'
import CaseDelayedChart from '@/modules/dashboardModule/widgets/chartWidget/CaseDelayedChart'
import sessionStore from '@/session/sessionStore'
import customConfigResource from '@/api/resources/ui/CustomConfigResource'
import { CASE_DELAYED_SCOPE, DASHBOARD_SITUATION_CASE_OPTIONS } from '@/api/resources/dashboard/dashboardService'
import { StoreUtils } from '@/shared/util/store-utils'

const CASE_DELAY_SUMMARY_ENUM = {
    GLOBAL_TARGET_RESPONSE_DATE: 'GLOBAL_TARGET_RESPONSE_DATE',
    DIAGNOSTIC_TARGET_RESPONSE_DATE: 'DIAGNOSTIC_TARGET_RESPONSE_DATE'
}

export default {
    name: 'ChartWidgetCaseSituation',
    components: {
        VtBubble,
        CaseDelayedChart
    },
    props: {
        item: { type: Object },
        caseTypes: { type: Array },
        update: { type: Boolean },
        filters: { type: Object }
    },
    data() {
        return {
            laboratoriesSelected: [],
            prioritiesSelected: [],
            caseTypesSelected: [],
            caseSituationsSelected: null,
            switchUpdateMode: false,
            noFilters: false
        }
    },
    computed: {
        caseSituationSelectedLabel() {
            return this.caseSituationsSelected ? this.$t(this.caseSituationsSelected.label) : this.$t(DASHBOARD_SITUATION_CASE_OPTIONS.ALL_PENDING_DASHBOARD.label)
        },
        uiComponent() {
            return this.item.uicomponent
        },
        caseDelaySummary() {
            return this.item.scope === CASE_DELAYED_SCOPE.DIAGNOSIS ? CASE_DELAY_SUMMARY_ENUM.DIAGNOSTIC_TARGET_RESPONSE_DATE : CASE_DELAY_SUMMARY_ENUM.GLOBAL_TARGET_RESPONSE_DATE
        },
        priorities() {
            return sessionStore.state.priorities
        },
        laboratories() {
            return StoreUtils.laboratories
        }
    },
    mounted() {
        this.getUiComponentDataAndFillChartData(this.uiComponent)
    },
    methods: {
        async getUiComponentDataAndFillChartData(selectedFilterUiComponent) {
            if (selectedFilterUiComponent === this.uiComponent) {
                const savedConfiguration = await customConfigResource.get(this.uiComponent)
                if (savedConfiguration && savedConfiguration.config) {
                    this.laboratoriesSelected = savedConfiguration.config.laboratoriesSelected
                    this.prioritiesSelected = savedConfiguration.config.prioritiesSelected
                    this.caseTypesSelected = savedConfiguration.config.caseTypesSelected
                    this.caseSituationsSelected = savedConfiguration.config.caseSituationsSelected
                    this.refreshCaseDelayedChartSelected()
                }
            }
        },
        refreshCaseDelayedChartSelected() {
            const filter = {
                'laboratory.labId': {
                    'filterType': 'set',
                    'values': this.laboratoriesSelected.length
                        ? this.laboratoriesSelected.map(laboratory => laboratory.labId) : this.laboratories.map(laboratory => laboratory.labId)
                },
                'priority.id': {
                    'filterType': 'set',
                    'values': this.prioritiesSelected.length
                        ? this.prioritiesSelected.map(priority => priority.id) : this.priorities.map(priority => priority.id)
                },
                'caseType.id': {
                    'filterType': 'set',
                    'values': this.caseTypesSelected.length
                        ? this.caseTypesSelected.map(caseType => caseType.id) : this.caseTypes.map(caseType => caseType.id)
                }
            }
            this.$refs?.caseDelayedChart?.fillData(filter, this.caseSituationsSelected?.key)
        },
        toggleWidgetConfigurationSidepanel() {
            this.$emit('toggleWidgetConfigurationSidepanel')
        }
    }
}
</script>

<style scoped>
    .help-text {
        font-size: 13px;
        line-height: 16px;
    }
    .filter-disabled {
        opacity: .5;
    }
</style>

<style lang="scss">
    .case-delayed-widget .small {
        min-height: 220px;
    }
</style>
